import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ArrowLeft, FileSignature } from "lucide-react";
import axios from "axios";
import { FIRM, firmContactLine } from "@/lib/firm";
import { numberToWords as numWords } from "@/lib/format";

const API = process.env.REACT_APP_BACKEND_URL;

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtLongDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }); }
  catch { return iso; }
};
const fmtMoney = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const amountWords = (n) => {
  const dollars = Math.floor(Number(n || 0));
  const words = numWords(dollars).replace(/ Dollars.*$/, "").trim() || "Zero";
  return `${words} U.S. Dollars`;
};

const Ctl = ({ label, value, onChange, type = "text", full = false, area = false, placeholder = "" }) => (
  <div className={full ? "md:col-span-2" : ""}>
    <label className="block text-[10px] uppercase tracking-[0.12em] text-[#8A8A8A] mb-1">{label}</label>
    {area ? (
      <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    ) : (
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="w-full bg-[#0F0F0A] border border-[#2A2A22] rounded-md px-3 py-2 text-[13px] text-[#ECECE4] focus:outline-none focus:border-[#B8860B]" />
    )}
  </div>
);

export default function MasterFundingAgreement() {
  const { appId } = useParams();
  const [f, setF] = useState({
    date: todayISO(),
    agreementNo: "",
    funderName: FIRM.name,
    funderAddress: FIRM.addressLines.join("\n"),
    counterpartyName: "",
    counterpartyAddress: "",
    purpose: "Refinance / payoff of the existing mortgage loan and funding of the related transaction",
    masterAmount: 0,
    existingLender: "",
    payoffAmount: 0,
    depositoryBank: "",
    depositoryAddress: "",
    signerName: FIRM.officer || "",
    signerTitle: "Authorized Officer",
    trustee: FIRM.trustee || "",
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    if (!appId) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/borrower/applications/${appId}`);
        const app = r.data.application || {};
        const b = app.binder || {};
        const short = String(app.id || "").replace(/-/g, "").slice(0, 8).toUpperCase();
        const yr = (b.closingDate || todayISO()).slice(0, 4);
        setF((p) => ({
          ...p,
          agreementNo: p.agreementNo || `STF-MFA-${yr}-${short}`,
          counterpartyName: b.borrowerName || p.counterpartyName,
          counterpartyAddress: b.borrowerAddress || p.counterpartyAddress,
          masterAmount: Number(b.newPrincipal || b.payoffAmount || 0) || p.masterAmount,
          existingLender: b.currentLender || p.existingLender,
          payoffAmount: Number(b.payoffAmount || 0) || p.payoffAmount,
        }));
      } catch { /* manual entry */ }
    })();
  }, [appId]);

  const bankLabel = f.depositoryBank || "[Depository / Settlement Bank — to be designated upon that bank's written execution]";

  return (
    <div className="binder-print-root min-h-screen bg-[#1a1a14] text-[#ECECE4]">
      <div className="no-print sticky top-0 z-20 bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#9A9A90] hover:text-white flex items-center gap-1.5 text-[12px]" data-testid="mfa-back-link">
              <ArrowLeft size={14} /> Back
            </Link>
            <Link to="/" className="text-[#9A9A90] hover:text-white text-[12px]" data-testid="mfa-home-link">Home</Link>
            <div className="flex items-center gap-2">
              <FileSignature size={16} className="text-[#B8860B]" />
              <span className="font-serif text-lg">Master Funding &amp; Settlement Agreement</span>
            </div>
          </div>
          <button onClick={() => window.print()} data-testid="mfa-print-btn"
            className="flex items-center gap-2 bg-[#B8860B] hover:bg-[#a0750a] text-[#13130E] font-semibold text-[12px] uppercase tracking-[0.12em] px-4 py-2 rounded-md">
            <Printer size={15} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="no-print bg-[#13130E] border-b border-[#2A2A22]">
        <div className="max-w-5xl mx-auto px-6 py-4 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="mfa-controls">
          <Ctl label="Agreement Date" type="date" value={f.date} onChange={set("date")} />
          <Ctl label="Agreement No." value={f.agreementNo} onChange={set("agreementNo")} />
          <Ctl label="Master Facility Amount (USD)" type="number" value={f.masterAmount} onChange={(v) => set("masterAmount")(Number(v))} />
          <Ctl label="Estimated Payoff (USD)" type="number" value={f.payoffAmount} onChange={(v) => set("payoffAmount")(Number(v))} />

          <Ctl label="Funder Name" value={f.funderName} onChange={set("funderName")} full />
          <Ctl label="Funder Address" value={f.funderAddress} onChange={set("funderAddress")} area full />

          <Ctl label="Borrower / Counterparty Name" value={f.counterpartyName} onChange={set("counterpartyName")} full />
          <Ctl label="Borrower / Counterparty Address" value={f.counterpartyAddress} onChange={set("counterpartyAddress")} area full />

          <Ctl label="Existing Lender (payoff target)" value={f.existingLender} onChange={set("existingLender")} full />
          <Ctl label="Purpose" value={f.purpose} onChange={set("purpose")} full />

          <div className="md:col-span-4 mt-1 rounded-md border border-[#B8860B]/30 bg-[#B8860B]/5 px-3 py-2 text-[11px] text-[#E4C97A]" data-testid="mfa-bank-warning">
            ⚠ Leave the Depository / Settlement Bank blank unless that bank has actually signed. Naming a bank here does not bind it — the agreement is not effective as to any bank until it executes the accession block below.
          </div>
          <Ctl label="Depository / Settlement Bank (only if it has agreed in writing)" value={f.depositoryBank} onChange={set("depositoryBank")} full placeholder="Leave blank until the bank executes" />
          <Ctl label="Depository Bank Address" value={f.depositoryAddress} onChange={set("depositoryAddress")} area full />

          <Ctl label="Signer Name" value={f.signerName} onChange={set("signerName")} />
          <Ctl label="Signer Title" value={f.signerTitle} onChange={set("signerTitle")} />
        </div>
      </div>

      <div className="print-area max-w-5xl mx-auto px-6 py-8 flex flex-col items-center gap-8">
        <div className="paper-sheet legal-page" data-testid="mfa-letter">
          <div className="mfa-head">
            <div className="mfa-firm">{f.funderName || FIRM.name}</div>
            <div className="mfa-firm-sub">{firmContactLine}</div>
          </div>
          <div className="lg-rule" />

          <div className="mfa-eyebrow">Master Funding &amp; Settlement Agreement</div>
          <h1 className="lg-title">Master Funding &amp; Settlement Agreement</h1>
          <div className="mfa-meta">
            <div><strong>Agreement No.:</strong> {f.agreementNo || "—"}</div>
            <div><strong>Dated:</strong> {fmtLongDate(f.date)}</div>
          </div>

          <p style={{ marginTop: 14 }}>
            This Master Funding &amp; Settlement Agreement (this "Agreement") is entered into as of {fmtLongDate(f.date)} by and between
            <strong> {f.funderName || FIRM.name}</strong> (the "Funder"), and <strong>{f.counterpartyName || "____________________"}</strong> (the "Borrower/Counterparty").
            It consolidates, and governs, the funding-package instruments listed in the Recitals into one master arrangement.
          </p>

          <div className="lg-h">Recitals — Funding Package Instruments</div>
          <p>This Agreement is the master document under which the following funding-package instruments are issued and administered:</p>
          <ol className="lg-ol">
            <li>The Private Negotiable Instrument (funding instrument);</li>
            <li>The Funding Source &amp; Security Agreement (and related UCC-1 financing statement);</li>
            <li>The Disbursement Authorization &amp; Payment Instruction; and</li>
            <li>The related closing binder for the transaction.</li>
          </ol>

          <div className="lg-h">1. Summary of Master Terms</div>
          <table className="lg-table"><tbody>
            <tr><td>Funder</td><td>{f.funderName || FIRM.name}</td></tr>
            <tr><td>Borrower / Counterparty</td><td>{f.counterpartyName || "—"}</td></tr>
            <tr><td>Purpose</td><td>{f.purpose || "—"}</td></tr>
            <tr><td>Master Facility Amount</td><td>USD {fmtMoney(f.masterAmount)} ({amountWords(f.masterAmount)})</td></tr>
            <tr><td>Existing Lender (to be paid off)</td><td>{f.existingLender || "—"}</td></tr>
            <tr><td>Estimated Payoff</td><td>USD {fmtMoney(f.payoffAmount)}</td></tr>
            <tr><td>Depository / Settlement Bank</td><td>{bankLabel}</td></tr>
          </tbody></table>

          <div className="lg-h">2. Funding Mechanics</div>
          <ol className="lg-ol">
            <li><strong>Source of Funds.</strong> Funding will be advanced by the Funder in immediately available funds from the Funder's own or committed capital. This Agreement does not authorize the presentment of any draft or instrument to any party as payment absent that party's acceptance and funding.</li>
            <li><strong>No Advance Until Closing.</strong> No funds are advanced, and no obligation of the existing lender is satisfied, unless and until the Funder actually disburses at closing to the designated payoff account.</li>
          </ol>

          <div className="lg-h">3. Settlement &amp; Depository Bank</div>
          <ol className="lg-ol">
            <li><strong>Designation.</strong> If a Depository / Settlement Bank is designated above, settlement of funded amounts will be effected through that bank's ordinary payment systems in accordance with the bank's own agreements and applicable law.</li>
            <li><strong>Bank Accession Required.</strong> No bank is a party to, bound by, or a guarantor of this Agreement, and this Agreement is <strong>not effective as to any bank</strong>, unless and until that bank executes the accession/acknowledgment block below. This Agreement, standing alone, confers no rights against, and no access to the payment systems of, any bank, the U.S. Treasury, or the Federal Reserve.</li>
          </ol>

          <div className="lg-h">4. Conditions Precedent</div>
          <ol className="lg-ol">
            <li>Satisfactory payoff figures from the existing lender/servicer, good through the funding date;</li>
            <li>Clear, marketable title and a first-priority funded lien upon closing;</li>
            <li>Completion of KYC/CIP and authority verification; and</li>
            <li>Execution and recordation of the loan/security documents.</li>
          </ol>

          <div className="lg-h">5. General</div>
          <ol className="lg-ol">
            <li><strong>No Third-Party Reliance.</strong> Except for a bank that executes the accession block, this Agreement confers no rights on any third party.</li>
            <li><strong>Entire Agreement.</strong> This Agreement, together with the funding-package instruments in the Recitals, is the entire master arrangement between the parties and supersedes prior understandings.</li>
            <li><strong>Governing Law.</strong> This Agreement is governed by the laws of the State of Delaware.</li>
          </ol>

          <div className="lg-sig-grid">
            <div>
              <div className="lg-sig-party">Funder</div>
              <div className="lg-sig-role">{f.funderName || FIRM.name}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">{f.signerName || "____________________"}, {f.signerTitle}</div>
              <div className="lg-sig-cap">Date: {fmtLongDate(f.date)}</div>
            </div>
            <div>
              <div className="lg-sig-party">Borrower / Counterparty</div>
              <div className="lg-sig-role">{f.counterpartyName || "____________________"}</div>
              <div className="lg-sig-rule" />
              <div className="lg-sig-cap">Signature</div>
              <div className="lg-sig-cap">Date: __________________</div>
            </div>
          </div>

          <div className="mfa-accession" data-testid="mfa-accession">
            <div className="lg-sig-party">Accession &amp; Acknowledgment — Depository / Settlement Bank</div>
            <div className="lg-sig-role">The undersigned bank, {bankLabel}, executes this block to become a party for settlement purposes only. Absent this signature, no bank is bound by this Agreement.</div>
            <div className="lg-sig-rule" />
            <div className="lg-sig-cap">By: __________________&nbsp;&nbsp;Name/Title: __________________&nbsp;&nbsp;Date: __________</div>
          </div>

          <p className="lg-note">Template / proposed agreement for review by licensed counsel before issuance. This Agreement is not itself a loan, an advance of funds, or a payment/negotiable instrument. It is not effective as to any bank, the U.S. Treasury, or the Federal Reserve, and does not represent that any of them is a party or guarantor, unless and until such party executes the accession block above. Nothing herein constitutes legal, tax, or financial advice.</p>
        </div>
      </div>

      <style>{`
        @media print {
          html, body { background:#fff !important; }
          .binder-print-root { background:#fff !important; color:#000 !important; min-height:0 !important; }
          .print-area { max-width:none !important; padding:0 !important; margin:0 !important; }
          .paper-sheet { box-shadow:none !important; margin:0 !important; page-break-after:auto; }
          .legal-page { padding:0.7in 0.8in !important; }
          .lg-ol > li, p, .lg-sig-grid, .mfa-accession { break-inside:avoid !important; page-break-inside:avoid !important; }
          .lg-table, tr { break-inside:avoid !important; page-break-inside:avoid !important; }
        }
        .paper-sheet { background:#fff; color:#0A0A0A; box-shadow:0 10px 40px rgba(0,0,0,.4); margin:0 auto; }
        .legal-page { width:8.5in; min-height:11in; padding:0.7in 0.8in; font-family:Georgia,'Times New Roman',serif; font-size:11pt; line-height:1.5; color:#111; }
        .mfa-head { text-align:center; }
        .mfa-firm { font-size:19pt; font-weight:700; letter-spacing:.18em; color:#0B4636; }
        .mfa-firm-sub { font-size:9pt; color:#444; margin-top:3px; }
        .lg-rule { height:2px; background:#0B4636; margin:12px 0 16px; }
        .mfa-eyebrow { font-size:9pt; letter-spacing:.16em; text-transform:uppercase; color:#0B4636; font-weight:700; }
        .lg-title { font-size:20pt; font-weight:700; margin:4px 0 0; line-height:1.15; }
        .mfa-meta { font-size:10.5pt; line-height:1.6; margin-top:6px; }
        .lg-h { font-weight:700; letter-spacing:.04em; margin:16px 0 6px; color:#0B4636; }
        .lg-ol { padding-left:20px; list-style:decimal outside; }
        .lg-ol > li { margin-bottom:8px; display:list-item; }
        .lg-note { font-size:9.5pt; font-style:italic; color:#555; margin-top:22px; border-top:1px solid #ddd; padding-top:10px; }
        .lg-table { width:100%; border-collapse:collapse; margin-top:8px; font-size:10.5pt; }
        .lg-table td { border:1px solid #d8d8d0; padding:6px 9px; }
        .lg-table td:first-child { background:#f6f6f1; font-weight:600; width:42%; }
        .lg-sig-grid { display:grid; grid-template-columns:1fr 1fr; gap:40px; margin-top:28px; }
        .lg-sig-party { font-weight:700; text-transform:uppercase; font-size:10pt; letter-spacing:.04em; }
        .lg-sig-role { font-size:9.5pt; color:#555; margin-bottom:22px; }
        .lg-sig-rule { border-bottom:1.2px solid #111; margin:2px 0 6px; }
        .lg-sig-cap { font-size:10pt; }
        .mfa-accession { margin-top:26px; border:1px dashed #7A5B12; border-radius:6px; padding:12px 14px; background:rgba(184,134,11,.05); }
        .mfa-accession .lg-sig-role { margin-bottom:16px; }
      `}</style>
    </div>
  );
}
